import { type InfiniteData, type QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import useDebounceFunction from '../../components/Hooks/useDebouncedFunction';
import { useApplicationContext } from '../../modules/context/ApplicationContext/ApplicationContext';
import { PageWithCursor } from '../../types/pagination.type';
import { UseInfiniteCursorDataQueryParams } from './useInfiniteCursorDataQuery.types';

const useInfiniteCursorDataQuery = <PageData>({
  limit,
  queryFn,
  queryKey,
  isEnabled,
  refetchOnMount,
  staleTime,
  gcTime,
  retry,
}: UseInfiniteCursorDataQueryParams<PageData>) => {
  const { team } = useApplicationContext();
  const { handleDebounceFunction: handleDebounceFetchNextPage } = useDebounceFunction({
    debounceTime: 1,
  });

  const queryKeyWithTeamId: QueryKey = [team?.id, limit, ...queryKey];

  const definedOverrides = {
    ...(refetchOnMount === undefined ? {} : { refetchOnMount }),
    ...(staleTime === undefined ? {} : { staleTime }),
    ...(gcTime === undefined ? {} : { gcTime }),
    ...(retry === undefined ? {} : { retry }),
  };

  const {
    data,
    error,
    refetch,
    isError,
    isLoading,
    isSuccess,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<
    PageWithCursor<PageData>,
    Error,
    InfiniteData<PageWithCursor<PageData>, string | null>,
    QueryKey,
    string | null
  >({
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    ...definedOverrides,
    queryKey: queryKeyWithTeamId,
    initialPageParam: null,
    queryFn: ({ pageParam, signal }) => queryFn({ cursor: pageParam, limit, signal }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const handleFetchNextPage = () => {
    if (hasNextPage) {
      return fetchNextPage();
    }
  };

  return {
    error,
    isError,
    refetch,
    isLoading,
    isSuccess,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    queryKey: queryKeyWithTeamId,
    pageParams: data?.pageParams,
    pages: data?.pages,
    pagesData: data?.pages?.flatMap((page) => page.data),
    fetchNextPage: () => handleDebounceFetchNextPage(handleFetchNextPage),
  };
};

export default useInfiniteCursorDataQuery;
